import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "../context/AlertContext";
import { useCreditContext } from "../context/CreditContext";
import { AiEngineList, AspectRatio, MenuList, PixVerseStyle, ViduStyle, useVideo } from "../context/juno/VideoContext";
import { useSound } from "../context/juno/SoundContext";
import { validateVideoParams } from "../utils/validation/videoValidation";
import { VideoMetadata } from "./useVideoMetadata";
import { useCheckCredit } from "./useCreditCheck";

type AiEngine = AiEngineList;

interface RequestBody {
  generatedVideoUuid: string;
  prompt: string;
  aiEngine: AiEngineList;
  action: MenuList;
  model: string;
  duration: number;
  aspectRatio: AspectRatio;
  loop: boolean;
  image1: string;
  image2: string;
  image3: string;
  style?: PixVerseStyle | ViduStyle;
  negativePrompt?: string;
  usageHistoryUuid?: string;
  steps?: number;
  // Extend Video params
  extendId?: string;
  extendType?: string;
  // ByteDance用のパラメータ
  video1?: string;
  audio1?: string;
  guidanceScale?: number;
  loopMode?: string;
  // Sync用のパラメータ
  syncMode?: string;
  // Upscaler用のパラメータ
  scale?: number;
  fps?: number;
  // Swaps用のパラメータ
  prompt2?: string;
  cameraMovement?: string | string[];
  movements?: string;
  // Google VEO3 Audio generation
  generateAudio?: boolean;
  // Bytedance Seedance camera fixed
  cameraFixed?: boolean;
}

export const useVideoGeneration = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const { checkCredit } = useCheckCredit();
  const { setAlert } = useAlert();
  const { setCreditTrigger } = useCreditContext();
  const { credit: soundCredit, isCalculatingCredit: isCalculatingSoundCredit } = useSound();

  const {
    setProcessing,
    aiEngine,
    modelVersion,
    duration,
    aspectRatios,
    lumaLoop,
    menu,
    image1,
    image2,
    image3,
    fetchVideoTrigger,
    setFetchVideoTrigger,
    styles,
    negativePrompt,
    inferenceSteps,
    guidanceScales,
    movements,
    // 動画と音声
    video1,
    audio1,
    // Extend Video params
    extend_id,
    extend_type,
    // ByteDance用のパラメータ
    loopMode,
    // Sync用のパラメータ
    syncMode,
    // Upscaler用のパラメータ
    scale,
    video1Metadata,
    fps,
    // Swaps用のパラメータ
    prompt2,
    lumaConcepts,
    // Google VEO3 Audio generation
    generateAudio,
    // Bytedance Seedance camera fixed
    cameraFixed,
  } = useVideo();

  const [updating, setUpdating] = useState(false);
  const [videoCredit, setVideoCredit] = useState<number | string | undefined>(undefined);
  const [isCalculatingVideoCredit, setIsCalculatingVideoCredit] = useState(false);
  const [totalCredit, setTotalCredit] = useState<number | string | undefined>(undefined);
  const [disable3sec, setDisable3sec] = useState(false);
  const [rotationDegree, setRotationDegree] = useState(0);

  useEffect(() => {
    if (disable3sec) {
      const timer = setTimeout(() => {
        setDisable3sec(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [disable3sec]);

  const fetchCredit = async (metadata?: VideoMetadata) => {
    const url = "/api/v1/juno/video-credit";
    const csrftoken = Cookies.get("csrftoken");
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-CSRFToken": csrftoken!,
    });

    interface CreditRequestBody {
      aiEngine: AiEngineList;
      model: string;
      action: MenuList;
      video1?: string;
      audio1?: string;
      scale?: number;
      duration?: number;
      metadata?: VideoMetadata;
      generateAudio?: boolean;
    }

    const currentModel = modelVersion[aiEngine];
    const body: CreditRequestBody = {
      aiEngine,
      model: currentModel,
      action: menu,
    };

    // メタデータが提供されている場合は優先して使用
    if (metadata) {
      body.metadata = metadata;
      // メタデータがある場合でも、音声が必要なケースは音声を送信
      if (["lipsync"].includes(menu) && audio1) body.audio1 = audio1;
    } else {
      // 従来の方法（後方互換性） - メタデータがない場合のみvideo1を送信
      if ((["lipsync", "upscale"].includes(menu) || menu == "v2v") && video1) body.video1 = video1;
      if (["lipsync"].includes(menu) && audio1) body.audio1 = audio1;
    }

    if (["upscale"].includes(menu) && scale) body.scale = scale;
    if (["t2v", "i2v"].includes(menu) && duration) body.duration = duration[aiEngine as AiEngine];
    if (aiEngine === "GOOGLE" && modelVersion[aiEngine] === "VEO3") {
      body.generateAudio = generateAudio;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    });

    return response.json();
  };

  // 動画クレジットの計算
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentModel = modelVersion[aiEngine];

      // クレジット計算の条件チェック
      const shouldCalculateCredit = () => {
        if (currentModel === "REALESRGAN" || currentModel === "TOPAZ_VIDEO_UPSCALE" || menu === "v2v") {
          return !!video1;
        } else if (aiEngine === "SYNC" || (aiEngine === "BYTEDANCE" && modelVersion[aiEngine] === "LATENTSYNC")) {
          return video1 && audio1;
        } else if (aiEngine === "BYTEDANCE" && modelVersion[aiEngine] === "SEEDANCE_V1_LITE") {
          return true;
        } else if (aiEngine === "HEDRA") {
          return audio1;
        }
        return true;
      };

      if (shouldCalculateCredit()) {
        setIsCalculatingVideoCredit(true);

        const metadataToSend =
          video1Metadata && "frame_count" in video1Metadata ? (video1Metadata as VideoMetadata) : undefined;

        fetchCredit(metadataToSend)
          .then((data) => {
            if (data.status === "success") {
              setVideoCredit(data.credit);
            }
            setIsCalculatingVideoCredit(false);
          })
          .catch(() => {
            setIsCalculatingVideoCredit(false);
          });
      } else {
        setVideoCredit(" - ");
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [aiEngine, modelVersion, duration, audio1, video1, video1Metadata, scale, generateAudio]);

  // 合計クレジットの計算
  useEffect(() => {
    if (typeof videoCredit !== "number" || typeof soundCredit !== "number") {
      setTotalCredit(videoCredit);
      return;
    }
    setTotalCredit(videoCredit + soundCredit);
  }, [videoCredit, soundCredit]);

  // プロンプト生成APIへのリクエスト（ダイス）
  const generateSampleText = async (setPrompt: (prompt: string) => void) => {
    try {
      setUpdating(true);
      if (!(await checkCredit())) return;
      setRotationDegree(rotationDegree + 360);
      setProcessing(true);

      const url = "/api/v1/juno/generate-video-prompt";
      const csrftoken = Cookies.get("csrftoken");
      const headers = new Headers({
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      });
      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({ aiEngine: aiEngine, modelVersion: modelVersion[aiEngine] }),
      });
      await response.json().then((data) => {
        setPrompt(data);
        setCreditTrigger(true);
      });
    } catch (e) {
      console.error(e);
    } finally {
      setProcessing(false);
      setUpdating(false);
    }
  };

  // プロンプト生成APIへのリクエスト（マジックブラシ）
  const updatePrompt = async (localPrompt: string, setPrompt: (prompt: string) => void) => {
    try {
      setUpdating(true);
      if (!(await checkCredit())) return;
      setProcessing(true);

      const url = "/api/v1/juno/update-video-prompt";
      const csrftoken = Cookies.get("csrftoken");
      const headers = new Headers({
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      });
      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          aiEngine: aiEngine,
          modelVersion: modelVersion[aiEngine],
          prompt: localPrompt,
        }),
      });
      await response.json().then((data) => {
        setPrompt(data);
        setCreditTrigger(true);
      });
    } catch (e) {
      console.error(e);
    } finally {
      setProcessing(false);
      setUpdating(false);
    }
  };

  const request_api_url = "/api/v1/juno/generate-video";

  const getHeaders = () => {
    const csrftoken = Cookies.get("csrftoken");
    return new Headers({
      "Content-Type": "application/json",
      "X-CSRFToken": csrftoken!,
    });
  };

  // リクエストボディの生成
  const buildRequestBody = (localPrompt: string, overrides = {}): RequestBody => {
    const body: RequestBody = {
      generatedVideoUuid: "",
      prompt: localPrompt,
      aiEngine,
      action: menu,
      model: modelVersion[aiEngine as AiEngine],
      duration: duration[aiEngine as AiEngine],
      aspectRatio: aspectRatios[aiEngine as AiEngine],
      loop: lumaLoop,
      steps: inferenceSteps[aiEngine as AiEngine],
      image1,
      image2,
      image3,
      guidanceScale: guidanceScales[aiEngine],
      ...overrides,
    };

    if (aiEngine === "RUNWAY" && modelVersion[aiEngine] === "GEN4_TURBO" && menu === "i2v") {
      body.image1 = image1;
      body.image2 = "";
    } else if (aiEngine === "PIXVERSE") {
      body.style = styles[aiEngine] as PixVerseStyle;
      body.negativePrompt = negativePrompt;
    } else if (aiEngine === "VIDU") {
      body.movements = movements[aiEngine];
      if (menu === "t2v" && modelVersion[aiEngine] === "VIDU_Q1") {
        body.style = styles[aiEngine] as ViduStyle;
      }
    } else if (aiEngine === "BYTEDANCE") {
      if (modelVersion[aiEngine] === "LATENTSYNC") {
        body.prompt = "";
        body.video1 = video1;
        body.audio1 = audio1;
        body.loopMode = loopMode;
      } else {
        body.cameraFixed = cameraFixed;
      }
    } else if (aiEngine === "HEDRA") {
      body.audio1 = audio1;
    } else if (aiEngine === "SYNC") {
      body.prompt = "";
      body.video1 = video1;
      body.audio1 = audio1;
      body.syncMode = syncMode;
    } else if (aiEngine === "REALESRGAN") {
      body.prompt = "";
      body.video1 = video1;
      body.scale = scale;
    } else if (aiEngine === "PIKA") {
      body.negativePrompt = negativePrompt;
      if (menu === "v2v") {
        body.video1 = video1;
        if (modelVersion[aiEngine] === "V2_SWAPS") {
          body.prompt2 = prompt2;
        }
      }
    } else if (aiEngine === "TOPAZLABS") {
      body.prompt = "";
      body.video1 = video1;
      body.scale = scale;
      body.fps = fps;
    } else if (aiEngine === "KLING") {
      body.negativePrompt = negativePrompt;
    } else if (aiEngine === "LUMA") {
      if (menu === "v2v") {
        body.video1 = video1;
      }
      if (modelVersion[aiEngine] === "RAY_2_REFRAME" || modelVersion[aiEngine] === "RAY_2_FLASH_REFRAME") {
        body.prompt = "";
      }
      if (modelVersion[aiEngine].includes("RAY_2")) {
        body.cameraMovement = lumaConcepts;
      }
    } else if (aiEngine === "FRAMEPACK") {
      body.negativePrompt = negativePrompt;
    } else if (aiEngine === "GOOGLE") {
      if (modelVersion[aiEngine] === "VEO3" && generateAudio) {
        body.generateAudio = generateAudio;
      }
    }

    if (extend_id && extend_type) {
      body.extendId = extend_id;
      body.extendType = extend_type;
    }
    return body;
  };

  const handleSubmit = async (
    localPrompt: string,
    setPrompt: (prompt: string) => void,
    setOpenProDialog: (open: boolean) => void
  ) => {
    try {
      console.log("useVideoGeneration handleSubmit called");
      setProcessing(true);
      setDisable3sec(true);

      const validationResult = validateVideoParams({
        aiEngine,
        modelVersion: modelVersion[aiEngine],
        menu,
        prompt: localPrompt,
        prompt2,
        image1,
        image2,
        image3,
        video1,
        video1Metadata,
        audio1,
        scale,
        extendId: extend_id,
      });

      if (!validationResult.isValid) {
        setDisable3sec(false);
        setAlert(
          "error",
          t(validationResult.errorKey!, validationResult.errorKey?.includes("size") ? { size: "16" } : undefined)
        );
        return;
      }

      const res = await createRequest(localPrompt);

      if (res.status === "error" && res.message === "UPGRADE_PLAN") {
        setOpenProDialog(true);
        return;
      }

      if (res.status === "error" && res.message === "NO_CREDITS") {
        setOpenProDialog(true);
        return;
      }

      if (res.status === "error" && res.message === "PROCESSING_LIMIT_EXCEEDED") {
        setAlert(
          "error",
          t("juno.error.processingLimit", {
            current: res.current,
            limit: res.limit,
          })
        );
        return;
      }

      setFetchVideoTrigger(!fetchVideoTrigger);
      setCreditTrigger(true);

      if (res.status === "success") {
        setAlert("success", t("juno.video.startGenerating"));
        if (onSuccess) {
          onSuccess();
        }
        try {
          await generateVideo(localPrompt, res.generated_video_uuid, res.usage_history_uuid);
        } catch (genError) {
          console.error("generateVideo error:", genError);
        }
      } else if (res.status === "error") {
        setAlert("error", t("juno.video.error.generate"));
      }
    } catch (e) {
      if (e instanceof SyntaxError && (e as SyntaxError).message.includes("Unexpected token '<'")) {
        console.warn("Request Timeout 524 (HTML Error Response Detected)");
      } else {
        console.error(e);
        setAlert("error", t("juno.video.error.generate"));
      }
    } finally {
      setFetchVideoTrigger(!fetchVideoTrigger);
      setCreditTrigger(true);
      setDisable3sec(false);
      setProcessing(false);
    }
  };

  // リクエスト生成
  const createRequest = async (localPrompt: string) => {
    const response = await fetch(request_api_url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(buildRequestBody(localPrompt)),
    });
    return response.json();
  };

  // 動画生成
  const generateVideo = async (localPrompt: string, generatedVideoUuid: string, usageHistoryUuid: string) => {
    const response = await fetch(request_api_url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(
        buildRequestBody(localPrompt, {
          generatedVideoUuid,
          usageHistoryUuid,
        })
      ),
    });

    const res = await response.json();

    if (res === "success") {
      setFetchVideoTrigger(!fetchVideoTrigger);
      setCreditTrigger(true);
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 0);
      }
    }
    return res;
  };

  return {
    updating,
    totalCredit,
    isCalculatingVideoCredit,
    isCalculatingSoundCredit,
    disable3sec,
    rotationDegree,
    generateSampleText,
    updatePrompt,
    handleSubmit,
  };
};
